import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ArrowLeft, Printer, CaretLeft, CaretRight } from "@phosphor-icons/react";
import { Link } from "react-router-dom";

const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const fmtHrs = (min) => `${Math.floor(min / 60)}:${String(min % 60).padStart(2, "0")}`;
const fmtDateShort = (iso) => {
  const d = new Date(iso + "T00:00:00");
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
};
const toIso = (d) => d.toISOString().slice(0, 10);
const mondayOf = (iso) => {
  const d = new Date(iso + "T00:00:00");
  const diff = d.getDay() === 0 ? -6 : 1 - d.getDay(); // JS Sunday=0
  d.setDate(d.getDate() + diff);
  return toIso(d);
};
const shiftWeek = (weekStart, deltaWeeks) => {
  const d = new Date(weekStart + "T00:00:00");
  d.setDate(d.getDate() + deltaWeeks * 7);
  return toIso(d);
};

export default function WeeklySnapshot() {
  const { user } = useAuth();
  const [weekStart, setWeekStart] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async (ws) => {
    setLoading(true);
    try {
      const q = ws ? `?week_start=${ws}` : "";
      const r = await api.get(`/reports/weekly-snapshot${q}`);
      setData(r.data);
      setWeekStart(r.data.week_start);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const prev = () => load(shiftWeek(weekStart, -1));
  const next = () => load(shiftWeek(weekStart, 1));
  const setFromPicker = (iso) => load(mondayOf(iso));

  return (
    <div className="min-h-screen bg-white print:bg-white">
      {/* Toolbar — hidden on print */}
      <div className="no-print border-b-2 border-slate-900 px-6 py-3 flex items-center justify-between gap-3 flex-wrap bg-slate-900 text-white">
        <div className="flex items-center gap-3">
          <Link to="/workforce-reports" className="brut-tag brut-tag-white inline-flex items-center gap-1" data-testid="ws-back">
            <ArrowLeft size={12} weight="bold" /> Back
          </Link>
          <div className="font-display uppercase tracking-tight">Weekly Payroll Snapshot</div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={prev} className="brut-tag brut-tag-white inline-flex items-center gap-1" data-testid="ws-prev">
            <CaretLeft size={12} weight="bold" /> Prev
          </button>
          <input
            type="date"
            value={weekStart || ""}
            onChange={(e) => e.target.value && setFromPicker(e.target.value)}
            className="brut-input text-slate-900"
            data-testid="ws-date-picker"
          />
          <button onClick={next} className="brut-tag brut-tag-white inline-flex items-center gap-1" data-testid="ws-next">
            Next <CaretRight size={12} weight="bold" />
          </button>
          <button onClick={() => window.print()} className="brut-btn brut-btn-primary px-4 py-2 inline-flex items-center gap-2" data-testid="ws-print">
            <Printer size={14} weight="bold" /> Print
          </button>
        </div>
      </div>

      {/* Printable body */}
      <div className="p-6 print:p-4 max-w-[1100px] mx-auto">
        {/* Header block */}
        <div className="mb-4 print:mb-3">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <div className="text-xs font-mono-ui tracking-widest text-slate-500 uppercase">// Payroll Review</div>
              <div className="font-display text-3xl uppercase tracking-tight">Weekly Payroll Snapshot</div>
              {data && (
                <div className="font-mono-ui text-sm mt-1" data-testid="ws-range-label">
                  Week of {fmtDateShort(data.week_start)} — {fmtDateShort(data.week_end)} ({data.week_start} → {data.week_end})
                </div>
              )}
            </div>
            <div className="text-right text-xs font-mono-ui text-slate-600">
              <div>Generated: {new Date().toLocaleString()}</div>
              <div>By: {user?.name} · US Operations</div>
            </div>
          </div>
        </div>

        {loading && <div className="text-center py-10 font-display uppercase text-slate-500">Loading…</div>}

        {data && !loading && (
          <>
            {data.rows.length === 0 ? (
              <div className="border-2 border-slate-900 p-10 text-center font-display uppercase text-slate-500" data-testid="ws-empty">
                No timesheets logged in this week.
              </div>
            ) : (
              <>
                <div className="border-2 border-slate-900 overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-slate-900 text-white">
                        <th className="text-left px-2 py-2 font-mono-ui text-[10px] uppercase tracking-widest">#</th>
                        <th className="text-left px-2 py-2 font-mono-ui text-[10px] uppercase tracking-widest">Worker</th>
                        <th className="text-left px-2 py-2 font-mono-ui text-[10px] uppercase tracking-widest">Code</th>
                        {data.week_dates.map((d, i) => (
                          <th key={d} className="text-right px-2 py-2 font-mono-ui text-[10px] uppercase tracking-widest">
                            <div>{WEEKDAYS[i]}</div>
                            <div className="opacity-70 font-normal">{fmtDateShort(d)}</div>
                          </th>
                        ))}
                        <th className="text-right px-2 py-2 font-mono-ui text-[10px] uppercase tracking-widest bg-blue-700">Total</th>
                        <th className="text-right px-2 py-2 font-mono-ui text-[10px] uppercase tracking-widest">P</th>
                        <th className="text-right px-2 py-2 font-mono-ui text-[10px] uppercase tracking-widest">A</th>
                        <th className="text-right px-2 py-2 font-mono-ui text-[10px] uppercase tracking-widest">S</th>
                        <th className="text-right px-2 py-2 font-mono-ui text-[10px] uppercase tracking-widest">L</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.rows.map((r, idx) => (
                        <tr key={r.staff_id} className="border-t border-slate-300" data-testid={`ws-row-${r.staff_code || r.staff_id}`}>
                          <td className="px-2 py-1.5 font-mono-ui text-xs text-slate-500">{idx + 1}</td>
                          <td className="px-2 py-1.5">
                            <div className="font-bold">{r.staff_name}</div>
                            {r.designation && <div className="text-[10px] text-slate-500">{r.designation}</div>}
                          </td>
                          <td className="px-2 py-1.5 font-mono-ui text-xs">{r.staff_code || "—"}</td>
                          {r.daily.map((d) => (
                            <td
                              key={d.date}
                              className={`px-2 py-1.5 text-right font-mono-ui ${d.attendance_status && d.attendance_status !== "present" ? "text-rose-700 italic" : ""}`}
                              title={d.attendance_status || ""}
                            >
                              {d.worked_minutes > 0
                                ? fmtHrs(d.worked_minutes)
                                : d.attendance_status === "absent" ? "A"
                                : d.attendance_status === "sick" ? "S"
                                : d.attendance_status === "leave" ? "L"
                                : "—"}
                            </td>
                          ))}
                          <td className="px-2 py-1.5 text-right font-mono-ui font-bold bg-blue-50" data-testid={`ws-total-${r.staff_code || r.staff_id}`}>
                            {fmtHrs(r.week_worked_minutes)}
                          </td>
                          <td className="px-2 py-1.5 text-right font-mono-ui text-xs">{r.present}</td>
                          <td className="px-2 py-1.5 text-right font-mono-ui text-xs">{r.absent}</td>
                          <td className="px-2 py-1.5 text-right font-mono-ui text-xs">{r.sick}</td>
                          <td className="px-2 py-1.5 text-right font-mono-ui text-xs">{r.leave}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="border-t-2 border-slate-900 bg-slate-100 font-bold">
                        <td colSpan={3} className="px-2 py-2 font-display uppercase">
                          Grand Total · {data.totals.worker_count} worker{data.totals.worker_count === 1 ? "" : "s"}
                        </td>
                        {data.week_dates.map((d) => {
                          const col = data.rows.reduce((s, r) => s + (r.daily.find((x) => x.date === d)?.worked_minutes || 0), 0);
                          return <td key={d} className="px-2 py-2 text-right font-mono-ui">{col > 0 ? fmtHrs(col) : "—"}</td>;
                        })}
                        <td className="px-2 py-2 text-right font-mono-ui bg-blue-100 text-base" data-testid="ws-grand-total">{fmtHrs(data.totals.worked_minutes)}</td>
                        <td className="px-2 py-2 text-right font-mono-ui">{data.totals.present}</td>
                        <td className="px-2 py-2 text-right font-mono-ui">{data.totals.absent}</td>
                        <td className="px-2 py-2 text-right font-mono-ui">{data.totals.sick}</td>
                        <td className="px-2 py-2 text-right font-mono-ui">{data.totals.leave}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>

                <div className="mt-3 text-[10px] font-mono-ui text-slate-500 flex flex-wrap gap-x-4 gap-y-1">
                  <span>P = Present</span>
                  <span>A = Absent</span>
                  <span>S = Sick</span>
                  <span>L = Leave</span>
                  <span>Hours shown as H:MM</span>
                </div>

                {/* Signature block */}
                <div className="mt-10 print:mt-8 grid grid-cols-2 gap-10">
                  <div>
                    <div className="border-t-2 border-slate-900 pt-2 text-xs font-mono-ui uppercase tracking-widest text-slate-600">Supervisor signature / date</div>
                  </div>
                  <div>
                    <div className="border-t-2 border-slate-900 pt-2 text-xs font-mono-ui uppercase tracking-widest text-slate-600">HR / Payroll signature / date</div>
                  </div>
                </div>
              </>
            )}
          </>
        )}
      </div>

      <style>{`
        @media print {
          .no-print { display: none !important; }
          @page { size: letter landscape; margin: 0.4in; }
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        }
      `}</style>
    </div>
  );
}
